import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { fmtTRY, fmtDate, todayISO } from "@/lib/format";
import { SectionCard, StatCard, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

// -- date helpers (reuse todayISO's Y-M-D convention) -----------------------
const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const lastDayOfMonth = (y, m0) => new Date(y, m0 + 1, 0).getDate();

function periodRange(preset) {
  const today = new Date();
  const y = today.getFullYear(); const m = today.getMonth();
  if (preset === "today") {
    const s = iso(today); return { start: s, end: s };
  }
  if (preset === "week") {
    const day = today.getDay(); // Sun=0 ... Sat=6
    const monOffset = day === 0 ? -6 : 1 - day; // week starts Monday
    const start = new Date(today); start.setDate(today.getDate() + monOffset);
    const end = new Date(start); end.setDate(start.getDate() + 6);
    return { start: iso(start), end: iso(end) };
  }
  if (preset === "month") {
    return { start: `${y}-${pad(m + 1)}-01`, end: `${y}-${pad(m + 1)}-${pad(lastDayOfMonth(y, m))}` };
  }
  if (preset === "lastMonth") {
    const lm = new Date(y, m - 1, 1);
    const ly = lm.getFullYear(); const lmi = lm.getMonth();
    return { start: `${ly}-${pad(lmi + 1)}-01`, end: `${ly}-${pad(lmi + 1)}-${pad(lastDayOfMonth(ly, lmi))}` };
  }
  if (preset === "year") {
    return { start: `${y}-01-01`, end: `${y}-12-31` };
  }
  return { start: "", end: "" };
}

const CASH_LABEL = { company: "Şirket Kasası", personal: "Şahsi Kasa" };
const TYPE_LABEL = { general: "Genel Şirket Gideri", customer_job: "Müşteri / İş Gideri" };
const TR_MONTHS = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
];
const PRESETS = [
  { key: "today", label: "Bugün" },
  { key: "week", label: "Bu Hafta" },
  { key: "month", label: "Bu Ay" },
  { key: "lastMonth", label: "Geçen Ay" },
  { key: "year", label: "Bu Yıl" },
  { key: "custom", label: "Özel" },
];

// "YYYY-MM-DD" → "DD.MM.YYYY"
const toTR = (iso) => {
  if (!iso || iso.length < 10) return iso || "";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
};

// Human-readable label for the currently active preset / custom range.
function periodLabel(preset, start, end) {
  const parseIso = (s) => {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  };
  if (preset === "today") return "Bugün";
  if (preset === "month" && start) {
    const d = parseIso(start);
    return `Bu Ay — ${TR_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  }
  if (preset === "lastMonth" && start) {
    const d = parseIso(start);
    return `Geçen Ay — ${TR_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  }
  if (preset === "year" && start) {
    return `Bu Yıl — ${start.slice(0, 4)}`;
  }
  if (preset === "week" && start && end) {
    const s = parseIso(start);
    const e = parseIso(end);
    const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
    if (sameMonth) {
      return `Bu Hafta — ${s.getDate()}–${e.getDate()} ${TR_MONTHS[e.getMonth()]} ${e.getFullYear()}`;
    }
    return `Bu Hafta — ${s.getDate()} ${TR_MONTHS[s.getMonth()]}–${e.getDate()} ${TR_MONTHS[e.getMonth()]} ${e.getFullYear()}`;
  }
  if (start && end) return `Seçili Dönem — ${toTR(start)}–${toTR(end)}`;
  if (start) return `Seçili Dönem — ${toTR(start)}`;
  if (end) return `Seçili Dönem — ${toTR(end)}`;
  return "Seçili Dönem";
}

export default function ExpenseReports() {
  const initial = periodRange("month");
  const [preset, setPreset] = useState("month");
  const [start, setStart] = useState(initial.start);
  const [end, setEnd] = useState(initial.end);
  const [category, setCategory] = useState("all");
  const [cash, setCash] = useState("all");
  const [expType, setExpType] = useState("all");
  const [customerId, setCustomerId] = useState("all");
  const [jobId, setJobId] = useState("all");
  const [currency, setCurrency] = useState("all");

  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, j, cat] = await Promise.all([
          api.get("/customers"), api.get("/jobs"), api.get("/categories"),
        ]);
        setCustomers(c.data || []); setJobs(j.data || []);
        setCategories(cat.data || []);
      } catch { /* silent */ }
    })();
  }, []);

  const load = async (payload) => {
    setLoading(true);
    try {
      const { data: d } = await api.post("/reports/expenses", payload);
      setData(d);
    } catch {
      toast.error("Gider raporu yüklenirken bir hata oluştu.");
    } finally { setLoading(false); }
  };

  const buildPayload = () => ({
    start_date: start || null,
    end_date: end || null,
    category: category !== "all" ? category : null,
    cash_account: cash !== "all" ? cash : null,
    expense_type: expType !== "all" ? expType : null,
    customer_id: customerId !== "all" ? customerId : null,
    job_id: jobId !== "all" ? jobId : null,
    currency: currency !== "all" ? currency : null,
  });

  // initial load — default "Bu Ay"
  useEffect(() => {
    load({
      start_date: initial.start, end_date: initial.end,
      category: null, cash_account: null, expense_type: null,
      customer_id: null, job_id: null, currency: null,
    });
    // eslint-disable-next-line
  }, []);

  const onPresetChange = (k) => {
    setPreset(k);
    if (k !== "custom") {
      const r = periodRange(k);
      setStart(r.start); setEnd(r.end);
    }
  };

  const applyFilters = () => load(buildPayload());
  const resetFilters = () => {
    const r = periodRange("month");
    setPreset("month"); setStart(r.start); setEnd(r.end);
    setCategory("all"); setCash("all"); setExpType("all");
    setCustomerId("all"); setJobId("all"); setCurrency("all");
    load({
      start_date: r.start, end_date: r.end,
      category: null, cash_account: null, expense_type: null,
      customer_id: null, job_id: null, currency: null,
    });
  };

  const jobsForFilter = useMemo(() => {
    if (customerId === "all") return jobs;
    return (jobs ?? []).filter((j) => j.customer_id === customerId);
  }, [jobs, customerId]);

  const currencies = data?.totals?.currencies ?? [];
  const multiCurrency = currencies.length > 1;

  const spansMultipleMonths = useMemo(() => {
    if (!start || !end) return false;
    return start.slice(0, 7) !== end.slice(0, 7);
  }, [start, end]);

  return (
    <div className="space-y-6" data-testid="expense-reports">
      {/* Filters */}
      <SectionCard title="Filtreler">
        <div className="p-5 space-y-4">
          <div className="flex flex-wrap gap-2" data-testid="expense-report-presets">
            {PRESETS.map((p) => (
              <Button
                key={p.key}
                variant={preset === p.key ? "default" : "outline"}
                size="sm"
                onClick={() => onPresetChange(p.key)}
                className={preset === p.key ? "bg-slate-900 hover:bg-slate-800" : ""}
                data-testid={`expense-preset-${p.key}`}
              >
                {p.label}
              </Button>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <FieldDate
              label="Başlangıç"
              value={start}
              onChange={(v) => { setStart(v); setPreset("custom"); }}
              testId="er-start-date"
            />
            <FieldDate
              label="Bitiş"
              value={end}
              onChange={(v) => { setEnd(v); setPreset("custom"); }}
              testId="er-end-date"
            />
            <SelectField label="Kategori" value={category} onChange={setCategory}
              options={[
                ["all", "Tümü"],
                ...(categories ?? []).map((c) => [
                  c.name,
                  c.status === "passive" ? `${c.name} (Pasif)` : c.name,
                ]),
              ]}
              testId="er-category-select" />
            <SelectField label="Kasa" value={cash} onChange={setCash}
              options={[["all", "Tümü"], ["company", "Şirket Kasası"], ["personal", "Şahsi Kasa"]]}
              testId="er-cash-select" />
            <SelectField label="Gider Türü" value={expType} onChange={setExpType}
              options={[["all", "Tümü"], ["general", "Genel Şirket Gideri"], ["customer_job", "Müşteri / İş Gideri"]]}
              testId="er-type-select" />
            <SelectField label="Müşteri" value={customerId} onChange={(v) => { setCustomerId(v); setJobId("all"); }}
              options={[["all", "Tümü"], ...(customers ?? []).map((c) => [c.id, c.name])]}
              testId="er-customer-select" />
            <SelectField label="İş / Proje" value={jobId} onChange={setJobId}
              options={[["all", "Tümü"], ...(jobsForFilter ?? []).map((j) => [j.id, j.customer_name ? `${j.customer_name} — ${j.name}` : j.name])]}
              testId="er-job-select" />
            <SelectField label="Para Birimi" value={currency} onChange={setCurrency}
              options={[["all", "Tümü"], ["TRY", "TRY (₺)"]]}
              testId="er-currency-select" />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={resetFilters} data-testid="er-reset">Sıfırla</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={applyFilters} disabled={loading} data-testid="er-apply">
              {loading ? "Hesaplanıyor..." : "Uygula"}
            </Button>
          </div>
        </div>
      </SectionCard>

      {/* Current Month Financial Summary (independent from filters) */}
      <SectionCard title={`Cari Ay Finansal Özeti — ${data?.current_month?.label || `${TR_MONTHS[new Date().getMonth()]} ${new Date().getFullYear()}`}`}>
        <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="er-current-month">
          {(data?.current_month?.by_currency ?? [{ currency: "TRY", income: 0, expense: 0, net: 0 }]).map((row) => (
            <div key={row.currency} className="col-span-1 md:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard
                label={multiCurrency ? `Bu Ay Gelir (${row.currency})` : "Bu Ay Gelir"}
                value={fmtTRY(row.income)}
                accent="emerald"
                testId={`er-cm-income-${row.currency}`}
              />
              <StatCard
                label={multiCurrency ? `Bu Ay Gider (${row.currency})` : "Bu Ay Gider"}
                value={fmtTRY(row.expense)}
                accent="red"
                testId={`er-cm-expense-${row.currency}`}
              />
              <StatCard
                label={multiCurrency ? `Net Nakit Akışı (${row.currency})` : "Net Nakit Akışı"}
                value={fmtTRY(row.net)}
                accent={row.net >= 0 ? "emerald" : "red"}
                testId={`er-cm-net-${row.currency}`}
              />
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Summary cards for filtered totals */}
      <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold -mb-2" data-testid="er-selected-period">
        Seçili dönem · {periodLabel(preset, start, end)}
      </div>
      {multiCurrency ? (
        <SectionCard title="Özet (Para Birimine Göre)">
          <div className="p-5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/50">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3 font-semibold">Para Birimi</th>
                  <th className="px-5 py-3 font-semibold text-right">Toplam Gider</th>
                  <th className="px-5 py-3 font-semibold text-right">Toplam Kayıt</th>
                  <th className="px-5 py-3 font-semibold text-right">Ortalama Gider</th>
                  <th className="px-5 py-3 font-semibold text-right">En Büyük Gider</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {currencies.map((c) => (
                  <tr key={c.currency}>
                    <td className="px-5 py-3 font-medium">{c.currency}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-rose-600">{fmtTRY(c.total)}</td>
                    <td className="px-5 py-3 text-right tabular-nums">{c.count}</td>
                    <td className="px-5 py-3 text-right tabular-nums">{fmtTRY(c.average)}</td>
                    <td className="px-5 py-3 text-right tabular-nums">{fmtTRY(c.largest)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard label="Toplam Gider" value={fmtTRY(data?.totals?.total || 0)} accent="rose" testId="er-total" />
          <StatCard label="Toplam Kayıt" value={data?.totals?.count || 0} accent="slate" testId="er-count" />
          <StatCard label="Ortalama Gider" value={fmtTRY(data?.totals?.average || 0)} accent="slate" testId="er-average" />
          <StatCard label="En Büyük Gider" value={fmtTRY(data?.totals?.largest || 0)} accent="slate" testId="er-largest" />
        </div>
      )}

      {/* Filtered expense list */}
      <SectionCard title="Gider Kayıtları">
        {(!data || (data.expenses ?? []).length === 0) ? (
          <EmptyState title="Seçili filtrelere uygun gider kaydı bulunamadı." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/50">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3 font-semibold">Tarih</th>
                  <th className="px-5 py-3 font-semibold">Açıklama</th>
                  <th className="px-5 py-3 font-semibold">Kategori</th>
                  <th className="px-5 py-3 font-semibold">Kasa</th>
                  <th className="px-5 py-3 font-semibold">Gider Türü</th>
                  <th className="px-5 py-3 font-semibold">Müşteri / İş / Proje</th>
                  <th className="px-5 py-3 font-semibold text-right">Tutar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(data.expenses ?? []).map((e) => (
                  <tr key={e.id} className="hover:bg-slate-50/60" data-testid={`er-row-${e.id}`}>
                    <td className="px-5 py-3 whitespace-nowrap">{fmtDate(e.date)}</td>
                    <td className="px-5 py-3 font-medium max-w-xs truncate">{e.description}</td>
                    <td className="px-5 py-3 text-slate-700">{e.category}</td>
                    <td className="px-5 py-3">{CASH_LABEL[e.cash_account] || e.cash_account}</td>
                    <td className="px-5 py-3 text-slate-700">{TYPE_LABEL[e.expense_type] || "-"}</td>
                    <td className="px-5 py-3">
                      {e.customer_name ? (
                        <>
                          <div className="text-slate-800">{e.customer_name}</div>
                          <div className="text-xs text-slate-500">{e.job_name || "—"}</div>
                        </>
                      ) : "—"}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums text-rose-600 font-medium">
                      {fmtTRY(e.amount)}{multiCurrency ? ` ${e.currency || "TRY"}` : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* Analyses grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SectionCard title="Kategori Analizi">
          <AnalysisTable
            testId="er-by-category"
            rows={data?.by_category ?? []}
            columns={[
              { key: "category", label: "Kategori" },
              { key: "count", label: "Kayıt", align: "right", format: (v) => v },
              { key: "total", label: "Toplam", align: "right", format: fmtTRY, tone: "rose" },
              { key: "percent", label: "%", align: "right",
                format: (v) => multiCurrency ? "—" : `%${(v || 0).toFixed(1)}` },
            ]}
            empty="Kategori verisi yok"
          />
        </SectionCard>

        <SectionCard title="Kasa Analizi">
          <AnalysisTable
            testId="er-by-cash"
            rows={data?.by_cash_account ?? []}
            columns={[
              { key: "label", label: "Kasa" },
              { key: "count", label: "Kayıt", align: "right" },
              { key: "total", label: "Toplam", align: "right", format: fmtTRY, tone: "rose" },
            ]}
            empty="Kasa verisi yok"
          />
        </SectionCard>

        <SectionCard title="Gider Türü Analizi">
          <AnalysisTable
            testId="er-by-type"
            rows={data?.by_expense_type ?? []}
            columns={[
              { key: "label", label: "Tür" },
              { key: "count", label: "Kayıt", align: "right" },
              { key: "total", label: "Toplam", align: "right", format: fmtTRY, tone: "rose" },
              { key: "percent", label: "%", align: "right",
                format: (v) => multiCurrency ? "—" : `%${(v || 0).toFixed(1)}` },
            ]}
            empty="Gider türü verisi yok"
          />
        </SectionCard>

        <SectionCard title="Müşteri Analizi">
          <AnalysisTable
            testId="er-by-customer"
            rows={data?.by_customer ?? []}
            columns={[
              { key: "customer_name", label: "Müşteri" },
              { key: "count", label: "Kayıt", align: "right" },
              { key: "total", label: "Toplam", align: "right", format: fmtTRY, tone: "rose" },
            ]}
            empty="Müşteriye bağlı gider yok"
          />
        </SectionCard>

        <SectionCard title="İş / Proje Analizi">
          <AnalysisTable
            testId="er-by-job"
            rows={data?.by_job ?? []}
            columns={[
              { key: "job_name", label: "İş / Proje" },
              { key: "customer_name", label: "Müşteri", format: (v) => v || "—" },
              { key: "count", label: "Kayıt", align: "right" },
              { key: "total", label: "Toplam", align: "right", format: fmtTRY, tone: "rose" },
            ]}
            empty="İş/projeye bağlı gider yok"
          />
        </SectionCard>

        {spansMultipleMonths && (
          <SectionCard title="Aylık Gider Analizi">
            <AnalysisTable
              testId="er-by-month"
              rows={data?.monthly ?? []}
              columns={[
                { key: "label", label: "Ay" },
                { key: "count", label: "Kayıt", align: "right" },
                { key: "total", label: "Toplam", align: "right", format: fmtTRY, tone: "rose" },
              ]}
              empty="Aylık veri yok"
            />
          </SectionCard>
        )}
      </div>
    </div>
  );
}

function AnalysisTable({ rows, columns, empty, testId }) {
  if (!rows || rows.length === 0) return <EmptyState title={empty} />;
  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <table className="w-full text-sm">
        <thead className="bg-slate-50/50">
          <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
            {columns.map((c) => (
              <th key={c.key} className={`px-5 py-3 font-semibold ${c.align === "right" ? "text-right" : ""}`}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r, idx) => (
            <tr key={idx}>
              {columns.map((c) => {
                const raw = r[c.key];
                const val = c.format ? c.format(raw) : raw;
                const tone = c.tone === "rose" ? "text-rose-600" : "";
                return (
                  <td key={c.key} className={`px-5 py-3 ${c.align === "right" ? "text-right tabular-nums " + tone : ""}`}>
                    {val ?? "—"}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FieldDate({ label, value, onChange, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-10 rounded-lg" data-testid={testId} />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-10 rounded-lg" data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>
          {(options ?? []).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
